//@ts-check
/**
 * i18n core — locale resolution, message merging, translation and Intl formatters.
 *
 * Deliberately free of Svelte and of Vite-only syntax: both UI apps import this same file
 * (aliased as `$i18n`), and the unit tests import it directly with no mocks. The reactive
 * "what locale are we in right now" part lives in each app's `lib/utils/locale.svelte.js`;
 * everything here is a pure function of its arguments.
 */

/**
 * @typedef {Record<string, string | Record<string, string>>} LocaleMessages
 *   One locale's flat key → message map. A message is a string, or an object of plural forms
 *   keyed by CLDR category ("one", "other", …).
 * @typedef {Record<string, LocaleMessages>} MessageBundle
 *   A loaded message module: locale tag → that locale's messages.
 * @typedef {Record<string, string | number>} Values
 *   `{placeholder}` values. A `count` also selects the plural form.
 * @typedef {{ languages?: string[], language?: string }} NavigatorLanguages
 *   The bits of `navigator` we read, so callers can pass a stand-in.
 * @typedef {ReturnType<typeof makeFormatters>} Formatters
 * @typedef {ReturnType<typeof makeTranslator>} TranslateFn
 */

/** The locale used when nothing better can be determined — including at prerender time. */
export const DEFAULT_LOCALE = "en-US";

/** Locales we ship messages for. Adding one is a message file plus an entry here. */
export const SUPPORTED_LOCALES = ["en-US", "es-MX"];

// ─── Locale resolution ────────────────────────────────────────────────────────

/**
 * Resolve any locale tag onto one we actually ship.
 *
 * Matching is exact first, then language-only: a browser set to `es-ES` or `es-AR` gets
 * `es-MX` rather than dropping all the way to English. That matters more with every locale
 * added, since region-exact matching would strand most speakers of a language we support.
 * @param {string} [tag] e.g. "es-AR"
 * @returns {string} a member of SUPPORTED_LOCALES
 */
export function resolveLocaleTag(tag) {
  if (!tag) return DEFAULT_LOCALE;

  let language;
  try {
    language = new Intl.Locale(tag).language;
  } catch {
    return DEFAULT_LOCALE; // malformed tag from a browser we don't control
  }

  const exact = SUPPORTED_LOCALES.find((l) => l.toLowerCase() === tag.toLowerCase());
  if (exact) return exact;

  const sameLanguage = SUPPORTED_LOCALES.find((l) => l.split("-")[0] === language);
  return sameLanguage || DEFAULT_LOCALE;
}

/**
 * Read the viewer's preferred locale.
 *
 * Returns DEFAULT_LOCALE when there is no navigator — which is the case during prerender,
 * where the pages are generated at build time with no viewer to ask. The prerendered HTML
 * is therefore English, and each app corrects it on mount (see locale.svelte.js).
 * @param {NavigatorLanguages} [nav] defaults to `navigator`
 * @returns {string} a member of SUPPORTED_LOCALES
 */
export function detectLocaleTag(nav) {
  const source = nav ?? (typeof navigator === "undefined" ? undefined : navigator);
  if (!source) return DEFAULT_LOCALE;
  const preferred = source.languages?.[0] || source.language;
  return resolveLocaleTag(preferred);
}

// ─── Message merging ──────────────────────────────────────────────────────────

/**
 * Pick one locale's slice out of a loaded message module, falling back to English.
 * @param {MessageBundle} messages locale-keyed module
 * @param {string} localeTag
 * @returns {LocaleMessages} that locale's slice, or the English one
 */
function sliceFor(messages, localeTag) {
  return messages?.[localeTag] || messages?.[DEFAULT_LOCALE] || {};
}

/**
 * Merge a feature's messages over the common ones for a locale.
 *
 * Two rules, both of which have been got wrong before and are covered by tests:
 *
 * 1. **The feature wins.** `common` is the shared fallback base, so a feature that defines
 *    its own wording for a shared key gets it. Merging the other way silently overrides
 *    every feature's specific wording with the generic one.
 * 2. **Nothing is mutated.** The message modules are singletons imported by every component,
 *    so merging into one would leak common keys into that module for the rest of the session.
 *    Always build a new object.
 * @param {string} localeTag
 * @param {MessageBundle} [featureMessages]
 * @param {MessageBundle} [commonMessages]
 * @returns {LocaleMessages} flat key → message for this locale
 */
export function mergeMessages(localeTag, featureMessages, commonMessages) {
  return {
    ...sliceFor(commonMessages, localeTag),
    ...sliceFor(featureMessages, localeTag),
  };
}

// ─── Translation ──────────────────────────────────────────────────────────────

/**
 * Fill `{placeholder}` slots from a values object. Anything with no matching value is left
 * alone rather than blanked, so a typo shows up as `{nmae}` on screen instead of vanishing.
 * @param {string} template
 * @param {Values} [values]
 * @returns {string} the template with its slots filled
 */
function interpolate(template, values) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => (Object.hasOwn(values, name) ? String(values[name]) : match));
}

/**
 * Pick the plural form for `count`.
 *
 * Plural messages are objects keyed by CLDR category, which is both what `Intl.PluralRules`
 * returns and what POEditor round-trips as a plural term:
 *
 *   "items_count": { "one": "{count} thing", "other": "{count} things" }
 *
 * Languages needing `zero`/`two`/`few`/`many` get them for free by adding the keys.
 * @param {Record<string, string>} forms
 * @param {number} count
 * @param {string} localeTag
 * @returns {string} the message for the matching plural category
 */
function selectPlural(forms, count, localeTag) {
  const category = new Intl.PluralRules(localeTag).select(count);
  return forms[category] ?? forms.other ?? forms.one ?? "";
}

/**
 * Look up one message.
 *
 * Presence is tested with `in`, not truthiness: a message deliberately set to "" must render
 * as empty, not fall through and print its own key.
 * @param {LocaleMessages} messages merged messages for the active locale
 * @param {string} key
 * @param {Values} [values] `{placeholder}` values; a `count` selects plural form
 * @param {{ fallback?: string, beautify?: boolean, localeTag?: string }} [opts]
 * @returns {string} the message for this locale, ready to render
 */
export function getString(messages, key, values, opts = {}) {
  const { fallback, beautify, localeTag = DEFAULT_LOCALE } = opts;

  let result;
  if (messages && key in messages) {
    const entry = messages[key];
    result =
      typeof entry === "object" && entry !== null ? selectPlural(entry, Number(values?.count ?? 0), localeTag) : entry;
  } else {
    result = fallback ?? key;
  }

  result = interpolate(String(result), values);
  return beautify ? capitalizeWords(result.replaceAll("_", " ")) : result;
}

/**
 * "no_results_found" → "No Results Found". For keys surfaced to users without a translation
 * of their own — a field name straight from an API, say.
 * @param {string} str
 * @returns {string} the string in Title Case
 */
function capitalizeWords(str) {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * Build the `t()` a component calls.
 * @param {LocaleMessages} messages merged messages for the active locale
 * @param {string} localeTag needed for plural category selection
 * @returns {TranslateFn} the component-facing t()
 * @type {(key: string, values?: Values, opts?: { fallback?: string, beautify?: boolean }) => string}
 */
export function makeTranslator(messages, localeTag) {
  return (key, values, opts) => getString(messages, key, values, { ...opts, localeTag });
}

// ─── Formatters ───────────────────────────────────────────────────────────────

/**
 * Intl formatters for a locale, built once per locale change rather than per call —
 * constructing an Intl formatter is the expensive part.
 *
 * `relative` is a function rather than a formatter because picking the unit is the useful
 * bit: a countdown reads "in 5 days", not "in 432000 seconds".
 * @param {string} localeTag
 * @param {string} [currencyCode]
 * @returns {Formatters} formatters bound to the locale
 */
export function makeFormatters(localeTag, currencyCode = "USD") {
  const date = new Intl.DateTimeFormat(localeTag, { dateStyle: "short" });
  const dateLong = new Intl.DateTimeFormat(localeTag, { dateStyle: "long" });
  const dateTime = new Intl.DateTimeFormat(localeTag, { dateStyle: "short", timeStyle: "short" });
  const number = new Intl.NumberFormat(localeTag, { maximumFractionDigits: 4 });
  const integer = new Intl.NumberFormat(localeTag, { maximumFractionDigits: 0 });
  const percent = new Intl.NumberFormat(localeTag, { style: "percent" });
  const money = new Intl.NumberFormat(localeTag, { style: "currency", currency: currencyCode });
  const moneyInt = new Intl.NumberFormat(localeTag, {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 0,
  });
  const relativeFormat = new Intl.RelativeTimeFormat(localeTag, { numeric: "auto" });

  /**
   * "in 5 days", "yesterday", "2 hours ago" — the unit is chosen from the size of the gap.
   * @param {Date|number} target
   * @param {Date|number} [from] defaults to now
   * @returns {string} e.g. "in 5 days"
   */
  function relative(target, from = Date.now()) {
    const seconds = (new Date(target).getTime() - new Date(from).getTime()) / 1000;
    const units = /** @type {const} */ ([
      ["year", 31536000],
      ["month", 2592000],
      ["week", 604800],
      ["day", 86400],
      ["hour", 3600],
      ["minute", 60],
    ]);
    for (const [unit, size] of units) {
      if (Math.abs(seconds) >= size) return relativeFormat.format(Math.round(seconds / size), unit);
    }
    return relativeFormat.format(Math.round(seconds), "second");
  }

  return { date, dateLong, dateTime, number, integer, percent, money, moneyInt, relative };
}

// ─── Currency ─────────────────────────────────────────────────────────────────

/**
 * Currency for a region, for when a price has no currency of its own. There is no
 * per-account currency setting in TingThings; when one arrives, it takes precedence
 * over this and this stays as the fallback.
 * @param {string} [region] e.g. "MX"
 * @returns {string} ISO 4217 code
 */
export function fallbackCurrencyCode(region) {
  const byRegion = { US: "USD", CA: "CAD", GB: "GBP", MX: "MXN", AU: "AUD", EU: "EUR" };
  return byRegion[region] || "USD";
}

/**
 * The symbol a locale uses for a currency ("$", "£", "€").
 * @param {string} currencyCode
 * @param {string} [localeTag]
 * @returns {string} the symbol, or the code itself if it has none
 */
export function currencySymbol(currencyCode, localeTag = DEFAULT_LOCALE) {
  const parts = new Intl.NumberFormat(localeTag, {
    style: "currency",
    currency: currencyCode,
  }).formatToParts(1);
  return parts.find((p) => p.type === "currency")?.value ?? currencyCode;
}
